import { ref, watch, Ref } from 'vue'

/**
 * Vue 3 localStorage 响应式封装 Hook
 *
 * @template T - 存储数据的类型
 *
 * @param {string} key - localStorage 存储的 key
 * @param {T} initialValue - 初始默认值（当 localStorage 没有值时使用）
 * @param {Serializer<T>} [serializer=defaultSerializer] - 序列化/反序列化策略（默认 JSON）
 *
 * @returns {Ref<T> & { remove: () => void, reset: () => void }}
 *   返回一个带有额外方法的 Ref 对象
 *
 * @description
 * 提供一个响应式的 localStorage 状态管理：
 * - 自动从 localStorage 读取初始值
 * - state 变化自动同步到 localStorage
 * - 支持 remove / reset 操作
 * - 支持自定义序列化器
 */
export type Serializer<T> = {
  read: (raw: string) => T
  write: (val: T) => string
}

const defaultSerializer: Serializer<any> = {
  read: (raw: string) => {
    try {
      return JSON.parse(raw)
    } catch {
      return raw
    }
  },
  write: (val: any) => JSON.stringify(val),
}

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  serializer: Serializer<T> = defaultSerializer as Serializer<T>,
): Ref<T> & { remove: () => void; reset: () => void } {
  const isClient = typeof window !== 'undefined'
  const state = ref<T>(initialValue) as Ref<T>

  const read = (): T => {
    if (!isClient) return initialValue

    try {
      const item = localStorage.getItem(key)
      if (item === null) return initialValue

      return serializer.read(item)
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  }

  const write = (val: T) => {
    if (!isClient) return

    try {
      localStorage.setItem(key, serializer.write(val))
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error)
    }
  }

  const remove = () => {
    if (!isClient) return

    try {
      localStorage.removeItem(key)
      state.value = initialValue
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error)
    }
  }

  const reset = () => {
    state.value = initialValue
    write(initialValue)
  }

  // 初始化状态
  if (isClient) {
    const initValue = read()
    state.value = initValue
  }

  // 监听状态变化并同步到 localStorage
  watch(
    state,
    (val) => {
      write(val)
    },
    { deep: true },
  )

  // 将 Ref 对象与额外方法合并返回
  return Object.assign(state, {
    remove,
    reset,
  }) as Ref<T> & { remove: () => void; reset: () => void }
}

export function setLocalStorage<T>(
  key: string,
  value: T,
  serializer: Serializer<T> = defaultSerializer as Serializer<T>,
) {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(key, serializer.write(value))
  } catch (error) {
    console.error(`Error setting localStorage key "${key}":`, error)
  }
}

export function getLocalStorage<T>(
  key: string,
  initialValue: T,
  serializer: Serializer<T> = defaultSerializer as Serializer<T>,
): T {
  if (typeof window === 'undefined') return initialValue

  try {
    const item = localStorage.getItem(key)
    if (item === null) return initialValue

    return serializer.read(item)
  } catch (error) {
    console.error(`Error getting localStorage key "${key}":`, error)
    return initialValue
  }
}

export function removeLocalStorage(key: string) {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error)
  }
}

// 特殊化的常用类型序列化器
export const stringSerializer: Serializer<string> = {
  read: (raw: string) => raw,
  write: (val: string) => val,
}

export const numberSerializer: Serializer<number> = {
  read: (raw: string) => Number(raw),
  write: (val: number) => String(val),
}

export const booleanSerializer: Serializer<boolean> = {
  read: (raw: string) => raw === 'true',
  write: (val: boolean) => String(val),
}
